"use client";

/**
 * Explorable vertical timeline — a pre-built learning interactive, not a quiz.
 * A dated spine runs top to bottom; each event's content (title, detail,
 * optional image + link) sits inline on the timeline, and the elapsed time
 * between events is stated in words ("32 years later") so the passage of time
 * reads clearly even when dates cluster. Scrolling every event into view
 * fires the completion beacon once.
 *
 * Accessibility: a plain ordered list — screen readers read it top to bottom,
 * focus order is natural, no custom keyboard handling. Images carry alt;
 * links get rel="noopener". Motion is a mount fade only, neutralized by the
 * global prefers-reduced-motion reset.
 */

import { CalendarRange, Check, ExternalLink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import AuthImage from "../AuthImage";
import ActivityFrame from "./ActivityFrame";
import { MathText } from "./StepRevealActivity";

export interface TimelineEvent {
  text: string;
  label: string;
  detail?: string;
  asset_id?: string;
  alt?: string;
  link?: { url: string; text: string };
}

/** First number in the label as a signed year, but ONLY when the label is
 * genuinely date-like — digits plus era markers/separators and nothing else.
 * "1885", "c. 1200 BCE", "500 BC", "1914–1918" parse; "Week 1", "Chapter 3",
 * "Prologue" return null (so they never fabricate an elapsed-time gap). */
export function parseYearLabel(label: string): number | null {
  const raw = (label ?? "").trim();
  const match = raw.match(/\d{1,4}/);
  if (!match) return null;
  // Strip digits, era words, and separators — anything left means it's a
  // named label that merely contains a number, not a date.
  const residue = raw
    .replace(/\d+/g, " ")
    .replace(/\b(b\.?c\.?e?|c\.?e|a\.?d|circa|c)\b/gi, " ")
    .replace(/[\s.,–—/'"()-]/g, "");
  if (residue) return null;
  const year = parseInt(match[0], 10);
  return /\bB\.?C\.?E?\b/i.test(raw) ? -year : year;
}

/** Human elapsed time between two date labels, e.g. "32 years later". Null
 * when either label has no parseable year or the gap isn't positive. */
export function elapsedLabel(from: string, to: string): string | null {
  const a = parseYearLabel(from);
  const b = parseYearLabel(to);
  if (a === null || b === null || b <= a) return null;
  const years = b - a;
  if (years === 1) return "1 year later";
  return `${years.toLocaleString()} years later`;
}

export default function TimelineActivity({
  courseId,
  title,
  prompt,
  steps,
  onComplete,
}: {
  // Needed only to render event images (auth-scoped assets); text-only
  // timelines (e.g. the public landing demo) omit it.
  courseId?: string;
  title: string;
  prompt?: string;
  steps: TimelineEvent[];
  // Called once when every event has been scrolled into view
  onComplete?: () => void;
}) {
  const [seen, setSeen] = useState<Set<number>>(() => new Set());
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const completed = useRef(false);

  const allSeen = steps.length > 0 && seen.size >= steps.length;

  useEffect(() => {
    if (steps.length === 0) return;
    // No IntersectionObserver (jsdom/old browsers): count everything explored
    if (typeof IntersectionObserver === "undefined") {
      setSeen(new Set(steps.map((_, i) => i)));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const newlySeen: number[] = [];
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.idx);
            newlySeen.push(idx);
            observer.unobserve(entry.target);
          }
        }
        if (newlySeen.length) {
          setSeen((prev) => {
            const next = new Set(prev);
            newlySeen.forEach((i) => next.add(i));
            return next;
          });
        }
      },
      { rootMargin: "0px 0px -20% 0px", threshold: 0.15 },
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
    // Re-observe only when the event count changes (refs are stable per index)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);

  useEffect(() => {
    if (allSeen && !completed.current) {
      completed.current = true;
      onComplete?.();
    }
  }, [allSeen, onComplete]);

  if (steps.length === 0) return null;

  return (
    <ActivityFrame
      type="timeline"
      kindLabel="Timeline"
      icon={CalendarRange}
      title={title}
      meta={
        allSeen ? (
          <span className="activity__done" role="status">
            <Check className="h-3.5 w-3.5" /> All explored
          </span>
        ) : (
          `${seen.size} / ${steps.length} explored`
        )
      }
    >
      {prompt && <p className="mb-3 text-sm text-muted">{prompt}</p>}

      <ol className="tlv">
        {steps.map((event, i) => {
          const gap = i > 0 ? elapsedLabel(steps[i - 1].label, event.label) : null;
          const detailParagraphs = (event.detail ?? "").split(/\n{2,}/).filter((p) => p.trim());
          return (
            <li
              key={i}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              data-idx={i}
              className={`tlv-item ${seen.has(i) ? "is-seen" : ""}`}
              style={{ "--tlv-i": i } as React.CSSProperties}
            >
              <div className="tlv-marker" aria-hidden="true">
                <span className="tlv-dot" />
              </div>
              <div className="tlv-card">
                <p className="tlv-head">
                  <span className="tlv-date">{event.label}</span>
                  {gap && <span className="tlv-gap">· {gap}</span>}
                </p>
                <p className="tlv-title">
                  <MathText text={event.text} />
                </p>
                {event.asset_id && courseId && (
                  <AuthImage
                    courseId={courseId}
                    assetId={event.asset_id}
                    alt={event.alt ?? ""}
                    className="tlv-img"
                  />
                )}
                {detailParagraphs.map((paragraph, p) => (
                  <p key={p} className="tlv-detail">
                    <MathText text={paragraph} />
                  </p>
                ))}
                {event.link && (
                  <a
                    href={event.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tlv-link"
                  >
                    {event.link.text}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </ActivityFrame>
  );
}
